import { useEffect, useRef } from 'react';
import { motion, useInView, useAnimation } from 'framer-motion';
import { FaGithub, FaExternalLinkAlt } from 'react-icons/fa';

// Custom technical CAD HUD SVG schematics for each system
const ProjectHeaderIcon = ({ sysRef }) => {
  if (sysRef === 'FLOW_01') {
    return (
      <svg className="w-full h-full bg-void" viewBox="0 0 320 180" xmlns="http://www.w3.org/2000/svg">
        {/* Grid Background */}
        <line x1="0" y1="90" x2="320" y2="90" stroke="#141414" strokeWidth="0.5" strokeDasharray="3 3" />
        <line x1="160" y1="0" x2="160" y2="180" stroke="#141414" strokeWidth="0.5" strokeDasharray="3 3" />
        
        {/* Lane perspective lines */}
        <path d="M 60,180 L 135,45 L 185,45 L 260,180" fill="none" stroke="#222" strokeWidth="1" />
        <line x1="160" y1="45" x2="160" y2="180" stroke="#333" strokeWidth="1" strokeDasharray="5 5" />
        
        {/* Tracked Vehicle 1 */}
        <motion.g
          initial={{ opacity: 0.8, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ repeat: Infinity, duration: 2, repeatType: 'reverse' }}
        >
          <rect x="85" y="105" width="50" height="35" fill="none" stroke="#ffffff" strokeWidth="1.2" />
          <path d="M 85,105 L 75,95 M 135,105 L 145,95 M 85,140 L 75,150 M 135,140 L 145,150" stroke="#444" strokeWidth="0.5" />
          <text x="90" y="117" className="fill-white-pure font-mono text-[6px] font-bold">ID: #FLOW_294</text>
          <text x="90" y="126" className="fill-grey-400 font-mono text-[5px]">VEL: 48 KM/H</text>
        </motion.g>
        
        {/* Tracked Vehicle 2 */}
        <g>
          <rect x="185" y="65" width="35" height="25" fill="none" stroke="#888" strokeWidth="0.8" />
          <text x="188" y="75" className="fill-white-pure font-mono text-[5px] font-bold">ID: #FLOW_295</text>
          <text x="188" y="83" className="fill-grey-400 font-mono text-[4px]">VEL: 52 KM/H</text>
        </g>
        
        {/* Camera target reticle */}
        <circle cx="160" cy="90" r="10" fill="none" stroke="#444" strokeWidth="0.5" />
        <path d="M 10,20 L 25,20 M 20,10 L 20,25" stroke="#666" strokeWidth="0.75" />
        <text x="30" y="17" className="fill-grey-600 font-mono text-[6px] font-bold">SYS_CAM_01 // VEHICLE_FLOW</text>
        
        {/* Live telemetry state */}
        <rect x="230" y="10" width="80" height="16" fill="none" stroke="#222" strokeWidth="1" />
        <circle cx="237" cy="18" r="2.5" className="fill-white-pure animate-pulse" />
        <text x="244" y="20" className="fill-white-pure font-mono text-[5px] font-bold">INFERENCE_ON</text>
      </svg>
    );
  }
  
  if (sysRef === 'SAFE_02') {
    return (
      <svg className="w-full h-full bg-void" viewBox="0 0 320 180" xmlns="http://www.w3.org/2000/svg">
        <line x1="0" y1="90" x2="320" y2="90" stroke="#141414" strokeWidth="0.5" strokeDasharray="3 3" />
        
        {/* Employee safety pose model wireframe representation */}
        <g transform="translate(130, 20)">
          {/* Head contour */}
          <circle cx="30" cy="25" r="9" fill="none" stroke="#444" strokeWidth="1" />
          
          {/* Hard Hat */}
          <motion.path
            d="M 19,23 C 19,11 41,11 41,23 Z"
            fill="none"
            stroke="#ffffff"
            strokeWidth="1.5"
            initial={{ opacity: 0.7 }}
            animate={{ opacity: 1 }}
            transition={{ repeat: Infinity, duration: 1.5, repeatType: 'reverse' }}
          />
          <line x1="30" y1="11" x2="30" y2="6" stroke="#ffffff" strokeWidth="1" />
          
          {/* Skeletal Joints */}
          <line x1="30" y1="34" x2="30" y2="95" stroke="#444" strokeWidth="1" />
          <line x1="30" y1="42" x2="8" y2="68" stroke="#444" strokeWidth="0.8" />
          <line x1="30" y1="42" x2="52" y2="68" stroke="#444" strokeWidth="0.8" />
          
          {/* Reflective vest blueprint overlay */}
          <path d="M 21,38 L 39,38 L 44,72 L 16,72 Z" fill="none" stroke="#ffffff" strokeWidth="1.2" strokeDasharray="2 1" />
          <line x1="30" y1="38" x2="30" y2="72" stroke="#ffffff" strokeWidth="1.2" />
          <line x1="21" y1="52" x2="39" y2="52" stroke="#ffffff" strokeWidth="1" />
          
          {/* Safety targets log */}
          <rect x="15" y="7" width="30" height="28" fill="none" stroke="#ffffff" strokeWidth="0.8" strokeDasharray="2 2" />
          <text x="49" y="14" className="fill-white-pure font-mono text-[5px] font-bold">HELMET: OK (98%)</text>
          
          <rect x="12" y="35" width="36" height="42" fill="none" stroke="#ffffff" strokeWidth="0.8" strokeDasharray="2 2" />
          <text x="52" y="42" className="fill-white-pure font-mono text-[5px] font-bold">VEST: OK (94%)</text>
        </g>
        
        {/* Compliance tags */}
        <text x="15" y="25" className="fill-grey-600 font-mono text-[6px] font-bold">SYS_CAM_04 // SITE_SAFETY</text>
        <text x="15" y="33" className="fill-white-pure font-mono text-[5px] tracking-wider">COMPLIANCE // SECURED</text>
      </svg>
    );
  }

  if (sysRef === 'CALL_03') {
    return (
      <svg className="w-full h-full bg-void" viewBox="0 0 320 180" xmlns="http://www.w3.org/2000/svg">
        <line x1="0" y1="90" x2="320" y2="90" stroke="#141414" strokeWidth="0.5" strokeDasharray="3 3" />
        
        {/* Streaming audio frequencies log */}
        <g transform="translate(15, 60)">
          {/* Grey raw signal */}
          <path d="M 0,30 L 15,30 L 20,5 L 25,50 L 30,10 L 35,45 L 40,25 L 45,35 L 50,30 L 70,30 L 75,10 L 80,55 L 85,2 L 90,48 L 95,30 L 120,30 L 125,15 L 130,42 L 135,28 L 140,30 L 170,30 L 180,-10 L 185,70 L 190,5 L 195,50 L 200,30 L 220,30 L 225,20 L 230,40 L 235,30 L 285,30" fill="none" stroke="#222" strokeWidth="1" />
          {/* White computed signal overlay */}
          <motion.path
            d="M 0,30 L 15,30 L 20,12 L 25,42 L 30,18 L 35,38 L 40,28 L 45,32 L 50,30 L 70,30 L 75,18 L 80,48 L 85,10 L 90,40 L 95,30 L 120,30 L 125,22 L 130,36 L 135,30 L 140,30 L 170,30 L 180,4 L 185,58 L 190,14 L 195,43 L 200,30 L 220,30 L 225,25 L 230,35 L 235,30 L 285,30"
            fill="none"
            stroke="#ffffff"
            strokeWidth="1.2"
            initial={{ pathLength: 0.9 }}
            animate={{ pathLength: 1 }}
            transition={{ repeat: Infinity, duration: 1, repeatType: 'reverse' }}
          />
        </g>
        
        {/* NLP diagnostic telemetry frame */}
        <g transform="translate(195, 18)">
          <rect x="0" y="0" width="110" height="36" fill="none" stroke="#222" strokeWidth="1" />
          <text x="6" y="11" className="fill-white-pure font-mono text-[5px] font-bold">NLP_SENTIMENT: +0.87</text>
          <text x="6" y="19" className="fill-grey-400 font-mono text-[4.5px]">TRANSCRIPTION ACTIVE...</text>
          <text x="6" y="27" className="fill-grey-600 font-mono text-[4.5px]">KEYWORD: "PIPELINE" (96%)</text>
          <circle cx="100" cy="8" r="2" className="fill-white-pure" />
        </g>

        {/* Audio HUD labels */}
        <text x="15" y="25" className="fill-grey-600 font-mono text-[6px] font-bold">STREAM_09 // AUDIO_INTEL</text>
        <text x="15" y="33" className="fill-white-pure font-mono text-[5.5px]">SPEECH_TO_TEXT // Whisper-base</text>
      </svg>
    );
  }
  
  return null;
};

const ProjectCard = ({ project, index, variants }) => {
  const cardRef = useRef(null);

  // Corner autolock reticle brackets
  const renderBrackets = () => (
    <>
      <span className="reticle-bracket top-0 left-0 border-t border-l border-white-pure group-hover:translate-x-0.5 group-hover:translate-y-0.5 group-hover:opacity-100" />
      <span className="reticle-bracket top-0 right-0 border-t border-r border-white-pure group-hover:-translate-x-0.5 group-hover:translate-y-0.5 group-hover:opacity-100" />
      <span className="reticle-bracket bottom-0 left-0 border-b border-l border-white-pure group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:opacity-100" />
      <span className="reticle-bracket bottom-0 right-0 border-b border-r border-white-pure group-hover:-translate-x-0.5 group-hover:-translate-y-0.5 group-hover:opacity-100" />
    </>
  );

  return (
    <motion.div
      ref={cardRef}
      variants={variants}
      className="project-card hud-card hud-card-hover group flex flex-col justify-between"
      whileHover={{ y: -2 }}
      transition={{ duration: 0.15 }}
    >
      {renderBrackets()}
      
      <div>
        {/* Project Thumbnail Icon Container (Desaturate-on-idle / contrast-on-hover) */}
        <div className="aspect-video relative overflow-hidden bg-void border-b border-grey-800 flex items-center justify-center">
          <ProjectHeaderIcon sysRef={project.sysRef} />
          
          <div className="absolute inset-0 bg-fine-noise opacity-5 pointer-events-none" />
          <div className="absolute bottom-4 left-4">
            <span className="px-2 py-1 bg-void/80 border border-grey-800 text-[8px] font-mono text-white-pure tracking-widest uppercase font-semibold">
              SYS_REF // {project.sysRef}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-xl font-display font-black text-white-pure uppercase group-hover:chrome-text">
              {project.title}
            </h3>
          </div>
          
          <p className="text-xs text-grey-400 font-sans leading-relaxed mb-6 text-left">
            {project.description}
          </p>
        </div>
      </div>

      {/* Tech tags and links */}
      <div className="p-6 border-t border-grey-800">
        <div className="flex flex-wrap gap-1.5 mb-6">
          {project.tags.map((tag) => (
            <span
              key={tag}
              className="px-2 py-1 bg-void border border-grey-800 text-[9px] font-mono text-grey-400"
            >
              {tag}
            </span>
          ))}
        </div>

        <div className="flex items-center justify-between font-mono text-[10px] tracking-wider border-t border-grey-800/50 pt-4">
          <a
            href={project.github}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-grey-400 hover:text-white-pure transition-colors"
          >
            <span className="text-grey-600">//</span> CODEBASE
          </a>
          {project.demo && (
            <a
              href={project.demo}
              className="flex items-center gap-1.5 text-grey-400 hover:text-white-pure transition-colors"
            >
              <FaExternalLinkAlt className="text-[9px]" />
              DEPLOYMENT
            </a>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const Projects = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.1 });
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) {
      controls.start('visible');
    }
  }, [isInView, controls]);

  const projects = [
    {
      title: 'Intelligent Vehicle Flow Analyzer',
      sysRef: 'FLOW_01',
      description: 'Award-winning computer vision system developed to compute real-time queue density, velocity indices, and municipal transport logs at traffic endpoints.',
      tags: ['YOLOv8', 'DeepSORT', 'FastAPI', 'React'],
      github: 'https://github.com/JerwinTitus2006',
      demo: '#',
    },
    {
      title: 'Smart Employee Safety Monitoring',
      sysRef: 'SAFE_02',
      description: 'Industrial safety compliance system tracking workplace safety gear (helmets, vests) in real-time, executing audio warnings via custom hardware protocols.',
      tags: ['YOLOv8', 'OpenCV', 'IoT Integration', 'FastAPI'],
      github: 'https://github.com/JerwinTitus2006',
      demo: '#',
    },
    {
      title: 'AI-Driven Call Intelligence Platform',
      sysRef: 'CALL_03',
      description: 'Natural language analysis pipeline designed to transcribe audio streams, compute sentiment indices, and extract structured logs using local NLP models.',
      tags: ['Whisper AI', 'NLP', 'FastAPI', 'Docker'],
      github: 'https://github.com/JerwinTitus2006',
      demo: '#',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const cardVariants = {
    hidden: { y: 25, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1],
      },
    },
  };

  return (
    <section
      id="projects"
      ref={sectionRef}
      className="section-padding relative overflow-hidden bg-void border-t border-grey-800"
    >
      <div className="container-custom relative z-10">
        
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={controls}
          className="mb-20 text-left border-l-2 border-white-primary pl-6"
        >
          <motion.span
            variants={cardVariants}
            className="inline-block text-[9px] font-mono font-bold tracking-[0.35em] uppercase text-grey-400 mb-2"
          >
            03 // PROJECT_REGISTRY
          </motion.span>
          <motion.h2
            variants={cardVariants}
            className="text-3xl sm:text-5xl md:text-6xl font-display font-black"
          >
            FEATURED // <span className="chrome-text">SYSTEMS</span>
          </motion.h2>
        </motion.div>

        {/* Project Cards Grid */}
        <motion.div 
          className="projects-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          variants={containerVariants}
          initial="hidden"
          animate={controls}
        >
          {projects.map((project, index) => (
            <ProjectCard
              key={index}
              project={project}
              index={index}
              variants={cardVariants}
            />
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Projects;
